import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTableGamePersistence } from '../../hooks/useTableGamePersistence';
import { TablePlayer, TableGameConfig } from '../../types/tableGame';
import { usePageTitle } from '../../hooks/usePageTitle';
import { SEOMeta } from '../../templates/SEOMeta';
import { logger } from '../../utils/logger';
import { useLocalizedNavigate } from '../../i18n/hooks';
import RecommendedSection from '../RecommendedSection';

const TableGame: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('tableGame.pageTitle'));
  
  const navigate = useLocalizedNavigate();
  const { tableId } = useParams<{ tableId: string }>();
  const { config: hookConfig, clearConfig } = useTableGamePersistence();

  // Estado local para forzar la carga inmediata desde localStorage
  const [config, setConfig] = useState<TableGameConfig | null>(null);
  
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showNextModal, setShowNextModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Cargar config directamente desde localStorage al montar
  useEffect(() => {
    const stored = localStorage.getItem('table_game_config');
    logger.log('Raw localStorage:', stored);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        logger.log('Parsed config:', parsed);
        setConfig(parsed);
      } catch (error) {
        logger.error('Error parsing config:', error);
        navigate('/table');
      }
    } else {
      logger.log('No config in localStorage');
      navigate('/table');
    }
  }, [navigate]);

  // Redirect if no config or wrong table ID
  useEffect(() => {
    logger.log('TableGame useEffect - config:', config);
    logger.log('TableGame useEffect - tableId from URL:', tableId);
    
    if (!config) {
      logger.log('No config found yet, waiting...');
      return;
    }
    
    if (config.tableId !== tableId) {
      logger.log('TableId mismatch:', config.tableId, '!=', tableId);
      navigate('/table');
      return;
    }
    
    logger.log('Config válida, players:', config.players);
  }, [config, tableId, navigate]);

  if (!config || !config.players.length) {
    logger.log('Rendering null - no config or no players');
    return null;
  }

  logger.log('Rendering game, currentPlayerIndex:', currentPlayerIndex);
  
  const currentPlayer = config.players[currentPlayerIndex];
  
  if (!currentPlayer) {
    logger.error('No currentPlayer found at index:', currentPlayerIndex);
    return null;
  }
  const nextPlayer = config.players[currentPlayerIndex + 1];
  const isLastPlayer = currentPlayerIndex === config.players.length - 1;

  const handleRevealCard = () => {
    setIsRevealed(!isRevealed);
  };

  const handleNext = () => {
    if (isLastPlayer) {
      setShowCompleteModal(true);
    } else {
      setShowNextModal(true);
    }
  };

  const handleContinueToNext = () => {
    setShowNextModal(false);
    setIsRevealed(false);
    setCurrentPlayerIndex(prev => prev + 1);
  };

  const handleRestart = () => {
    setShowCompleteModal(false);
    navigate('/table');
  };

  return (
    <>
      <SEOMeta
        title={t('tableGame.seo.title')}
        description={t('tableGame.seo.description')}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950 p-4 flex items-center justify-center">
      <div className="max-w-md w-full">
        <RecommendedSection position="top" />
        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {t('tableGame.progress.playerOf', { current: currentPlayerIndex + 1, total: config.players.length })}
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {Math.round(((currentPlayerIndex + 1) / config.players.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-500 ease-out"
              style={{ width: `${((currentPlayerIndex + 1) / config.players.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 mb-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              {t('tableGame.turnOf', { name: currentPlayer.name })}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {t('tableGame.tapCardHint')}
            </p>
          </div>

          {/* Reveal Card */}
          <div
            onClick={handleRevealCard}
            className={`
              relative w-full aspect-[3/4] rounded-xl cursor-pointer
              transition-all duration-500 transform preserve-3d
              ${isRevealed ? 'rotate-y-180' : ''}
              hover:scale-105
            `}
            style={{
              transformStyle: 'preserve-3d',
              transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Card Back */}
            <div
              className={`
                absolute inset-0 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600
                flex items-center justify-center shadow-xl
                ${isRevealed ? 'opacity-0' : 'opacity-100'}
                transition-opacity duration-300
              `}
              style={{ backfaceVisibility: 'hidden' }}
            >
              <div className="text-center text-white">
                <div className="text-6xl mb-4">🎭</div>
                <p className="text-xl font-bold">{t('tableGame.cardBack.tapToReveal')}</p>
              </div>
            </div>

            {/* Card Front */}
            <div
              className={`
                absolute inset-0 rounded-xl flex flex-col items-center justify-center p-6 shadow-xl
                ${currentPlayer.role === 'impostor' 
                  ? 'bg-gradient-to-br from-red-500 to-pink-600' 
                  : 'bg-gradient-to-br from-green-500 to-emerald-600'
                }
                ${isRevealed ? 'opacity-100' : 'opacity-0'}
                transition-opacity duration-300
              `}
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <div className="text-center text-white">
                <div className="text-7xl mb-4">
                  {currentPlayer.role === 'impostor' ? '🎭' : '✅'}
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  {currentPlayer.role === 'impostor' ? t('tableGame.cardFront.impostor') : t('tableGame.cardFront.player')}
                </h3>
                {currentPlayer.word && (
                  <div className="bg-white/20 dark:bg-black/20 rounded-lg p-4 backdrop-blur-sm">
                    <p className="text-sm mb-1 opacity-90">{t('tableGame.cardFront.yourWord')}</p>
                    <p className="text-2xl font-bold">{currentPlayer.word}</p>
                  </div>
                )}
                {currentPlayer.role === 'impostor' && (
                  <p className="text-sm opacity-90 mt-4">
                    {t('tableGame.cardFront.impostorHint')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Next Button - Siempre visible para privacidad */}
          <div className="mt-6">
            <button
              onClick={handleNext}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-purple-600 
                         hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl
                         shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
              {isLastPlayer 
                ? t('tableGame.next.finish')
                : t('tableGame.next.passTo', { name: nextPlayer?.name })
              }
            </button>
            {!isRevealed && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                {t('tableGame.next.revealFirst')}
              </p>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white bg-opacity-70 dark:bg-gray-900/70 backdrop-blur-sm rounded-xl p-4 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('tableGame.instructions.privacy')}
          </p>
        </div>
      </div>

      {/* Next Player Modal */}
      {showNextModal && nextPlayer && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {}}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-8 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🔄</div>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                {t('tableGame.modalNext.title', { name: nextPlayer.name })}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('tableGame.modalNext.body', { name: nextPlayer.name })}
              </p>
              <button
                onClick={handleContinueToNext}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 
                           hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl
                           shadow-lg transition-all duration-200"
              >
                {t('tableGame.modalNext.continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {}}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-8 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">
                {t('tableGame.modalComplete.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('tableGame.modalComplete.body')}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleRestart}
                  className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 
                             hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl
                             shadow-lg transition-all duration-200"
                >
                  {t('tableGame.modalComplete.newGame')}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 px-6 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-100 
                             font-medium rounded-xl transition-all duration-200"
                >
                  {t('tableGame.modalComplete.goHome')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        <RecommendedSection position="bottom" />
    </div>
    </>
  );
};

export default TableGame;

